// 逐題作答紀錄：users/{uid}/answers/{科目__主題__題目ID}，每題一筆，重複作答時更新
// 用途：完成率（答對過的題數）、錯題率（曾答錯的題數）、「曾錯過的題目」練習
// 訪客存在 localStorage（gsat_answers）
import { collection, doc, getDocs, increment, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { logActivity } from './activityLog';

export type QRef = { s: string; t: string; id: string };
export type AnswerStat = {
  s: string; t: string; id: string;
  attempts: number;     // 作答次數
  corrects: number;     // 答對次數
  wrongs: number;       // 答錯次數
  lastCorrect: boolean; // 最後一次是否答對
  ts?: number;          // 最後作答時間（毫秒）；舊紀錄沒有
};
export type AnswerMap = Record<string, AnswerStat>;

const LS_KEY = 'gsat_answers';
export const answerKey = (r: QRef) => `${r.s}__${r.t}__${r.id}`;

const readLocal = (): AnswerMap => {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
};

export async function loadAnswers(uid: string | null): Promise<AnswerMap> {
  if (!uid) return readLocal();
  const snap = await getDocs(collection(db, 'users', uid, 'answers'));
  const map: AnswerMap = {};
  snap.docs.forEach(d => {
    const v = d.data() as Partial<AnswerStat>;
    map[d.id] = { s: v.s!, t: v.t!, id: v.id!, attempts: v.attempts ?? 0, corrects: v.corrects ?? 0, wrongs: v.wrongs ?? 0, lastCorrect: !!v.lastCorrect, ts: (v as any).ts?.toMillis?.() };
  });
  return map;
}

// 在記憶體中的紀錄上套用一次作答（畫面立即更新用）
export function applyAnswer(map: AnswerMap, ref: QRef, correct: boolean): AnswerMap {
  const k = answerKey(ref);
  const cur = map[k] ?? { ...ref, attempts: 0, corrects: 0, wrongs: 0, lastCorrect: false };
  return { ...map, [k]: {
    ...cur,
    attempts: cur.attempts + 1,
    corrects: cur.corrects + (correct ? 1 : 0),
    wrongs: cur.wrongs + (correct ? 0 : 1),
    lastCorrect: correct,
    ts: Date.now(),
  } };
}

// 寫入一次作答
export async function saveAnswer(uid: string | null, ref: QRef, correct: boolean) {
  logActivity(uid, ref, correct).catch(() => {});   // 每日活動（日曆用），失敗不影響作答
  if (!uid) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(applyAnswer(readLocal(), ref, correct))); } catch {}
    return;
  }
  await setDoc(doc(db, 'users', uid, 'answers', answerKey(ref)), {
    ...ref,
    attempts: increment(1),
    corrects: increment(correct ? 1 : 0),
    wrongs: increment(correct ? 0 : 1),
    lastCorrect: correct,
    ts: serverTimestamp(),
  }, { merge: true });
}

// 上線時把現有錯題本的題目標記為「曾答錯」（只補沒有紀錄的題目）
export async function seedFromWrongs(uid: string | null, map: AnswerMap, refs: QRef[]): Promise<AnswerMap> {
  const missing = refs.filter(r => !map[answerKey(r)]);
  if (!missing.length) return map;
  let next = map;
  for (const r of missing) next = { ...next, [answerKey(r)]: { ...r, attempts: 1, corrects: 0, wrongs: 1, lastCorrect: false } };
  if (!uid) {
    try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
  } else {
    await Promise.all(missing.map(r => setDoc(doc(db, 'users', uid, 'answers', answerKey(r)),
      { ...r, attempts: 1, corrects: 0, wrongs: 1, lastCorrect: false, ts: serverTimestamp() }, { merge: true })));
  }
  return next;
}
